"use client";

import { type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import {
  SidebarGroup,
  SidebarGroupContent,
  // SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useUIStore } from "@/store/uiStore";

export type Item = {
  title: string;
  url?: string;
  icon?: LucideIcon;
  tooltip?: string;
  description?: string;
  children?: Item[];
  isActive?: boolean;
  badge?: string | React.ReactNode;
  highlight?: boolean;
};

export function NavMain({
  items,
  ...props
}: { items: Item[] } & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const navigationLock = useUIStore((state) => state.navigationLock);

  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent className="flex flex-col gap-2">
        {/* <SidebarGroupLabel>Knowledge Base</SidebarGroupLabel> */}
        <SidebarMenu className="gap-2">
          {items.map((item) => (
            <SidebarMenuItem key={item.title}>
              {item.url ? (
                <SidebarMenuButton
                  tooltip={item.tooltip}
                  disabled={navigationLock.isLocked}
                  className={`font-medium ${item.highlight ? "bg-yellow-300" : ""}`}
                  asChild
                >
                  <Link to={item.url} className="font-medium flex items-center gap-2">
                    {item.icon && <item.icon color={item.badge ? "var(--primary)" : "currentColor"}/>}
                    <span className="flex-1">{item.title}</span>
                    {item.badge && (
                      <Badge variant="default" className="ml-auto text-[10px] px-1.5 py-0 h-4">
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                </SidebarMenuButton>
              ) : (
                <SidebarMenuButton
                  tooltip={item.tooltip}
                  disabled={navigationLock.isLocked}
                  className="font-medium"
                >
                  <div className="font-medium flex items-center gap-2 w-full">
                    {item.icon && <item.icon  className="h-4 w-4"/>}
                    <span className="flex-1">{item.title}</span>
                    {item.badge && (
                      <Badge variant="default" className="ml-auto text-[10px] px-1.5 py-0 h-4">
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                </SidebarMenuButton>
              )}
              {item.children?.length ? (
                <SidebarMenuSub className="ml-5 border-l-0 px-1.5">
                  {item.children.map((child) => (
                    <SidebarMenuSubItem key={child.title}>
                      {child.url ? (
                        <SidebarMenuSubButton 
                          asChild
                          isActive={child.isActive}
                        >
                          <Link 
                            to={child.url}
                            className={
                              'truncate ' + (navigationLock.isLocked ? "pointer-events-none opacity-50" : "")
                            }
                          >
                            {child.icon && <child.icon className="h-4 w-4" />}
                            {child.title}
                          </Link>
                        </SidebarMenuSubButton>
                      ) : (
                        <SidebarMenuSubButton 
                          isActive={child.isActive}
                          className={navigationLock.isLocked ? "opacity-50 cursor-not-allowed" : ""}
                        >
                          <div className="flex items-center gap-2">
                            {child.icon && <child.icon className="h-4 w-4 animate-spin" />}
                            {child.title}
                          </div>
                        </SidebarMenuSubButton>
                      )}
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              ) : null}
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
